import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";

import Button from "../../../../components/button/Button";
import { SettingsDropdown } from "../../../../components/dropdown/SettingsDropdown";
import { SettingsInput } from "../../../../components/inputs/SettingsInput";
import { Modal } from "../../../../components/modal/Modal";
import { WriteWithAi } from "../../../ai/WriteWithAi";
import { EMAIL_TEMPLATE_PLACEHOLDERS } from "./emailTemplates.constants";
import { EmailTemplate, EmailTemplatePayload } from "./emailTemplates.types";

type Props = {
  template: EmailTemplate | null;
  onClose: () => void;
  onSubmit: (values: EmailTemplatePayload) => void;
  isSubmitting: boolean;
};

export const EmailTemplateModal: React.FC<Props> = ({
  template,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const editorRef = useRef<ReactQuill | null>(null);
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [body, setBody] = useState(template?.body || "");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isEditing = Boolean(template);
  const isNameInvalid = isSubmitted && !name.trim();
  const isSubjectInvalid = isSubmitted && !subject.trim();

  useEffect(() => {
    setName(template?.name || "");
    setSubject(template?.subject || "");
    setBody(template?.body || "");
    setIsSubmitted(false);
  }, [template]);

  const insertPlaceholder = (
    placeholder: typeof EMAIL_TEMPLATE_PLACEHOLDERS[number],
  ) => {
    const editor = editorRef.current?.getEditor();

    if (!editor) {
      setBody((currentBody) => `${currentBody}${placeholder.token}`);
      return;
    }

    const range = editor.getSelection(true);
    editor.insertText(range?.index || 0, placeholder.token, "user");
  };

  const handleSubmit = () => {
    setIsSubmitted(true);

    if (!name.trim() || !subject.trim()) {
      return;
    }

    onSubmit({ name, subject, body });
  };

  return (
    <Modal onClose={onClose} width={"920px"}>
      <div className={"font-Manrope"}>
        <div className={"mb-6"}>
          <div className={"text-xl font-semibold text-[#1D2C3B]"}>
            {isEditing ? "Edit Email Template" : "New Email Template"}
          </div>
          <div className={"mt-1 text-sm text-[#5F758F]"}>
            Create a reusable draft for compose email.
          </div>
        </div>

        <div className={"grid gap-5"}>
          <SettingsInput
            label={"Template name"}
            value={name}
            placeholder={"Quarterly bookkeeping"}
            inputClasses={"w-full"}
            isError={isNameInvalid}
            onChange={(event) => setName(event.target.value)}
          />

          <SettingsInput
            label={"Subject"}
            value={subject}
            placeholder={"Documents needed"}
            inputClasses={"w-full"}
            isError={isSubjectInvalid}
            onChange={(event) => setSubject(event.target.value)}
          />

          <div>
            <div
              className={
                "mb-2 flex flex-wrap items-center justify-between gap-3"
              }>
              <div className={"text-sm text-[#5F758F]"}>Message</div>
              <div className={"flex items-center gap-3"}>
                <WriteWithAi
                  purpose={"email"}
                  getHtml={() => body}
                  onUseHtml={setBody}
                  subject={subject}
                  onSubject={setSubject}
                />
                <SettingsDropdown
                  actionsList={EMAIL_TEMPLATE_PLACEHOLDERS.map(
                    (placeholder) => ({
                      name: placeholder.label,
                      action: () => insertPlaceholder(placeholder),
                    }),
                  )}>
                  <button
                    type={"button"}
                    className={
                      "text-sm font-semibold text-[#276EF1] underline-offset-2 hover:underline"
                    }>
                    Add a placeholder
                  </button>
                </SettingsDropdown>
              </div>
            </div>

            <div
              className={"overflow-hidden rounded-xl border border-[#C8D2DE]"}>
              <ReactQuill
                ref={editorRef}
                theme={"snow"}
                value={body}
                onChange={setBody}
                className={"min-h-[340px] [&_.ql-container]:min-h-[292px]"}
              />
            </div>
          </div>
        </div>

        <div className={"mt-6 flex justify-end gap-3"}>
          <Button
            label={"Cancel"}
            buttonType={"button"}
            colorType={"outline"}
            size={"custom"}
            customSize={"h-10 min-h-[40px] px-6"}
            onClick={onClose}
          />
          <Button
            label={isEditing ? "Save Changes" : "Create Template"}
            buttonType={"button"}
            size={"custom"}
            customSize={"h-10 min-h-[40px] px-6"}
            onClick={handleSubmit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
};
